import { useState } from "react";
import { useCentrosCusto, CentroCusto, CentroCustoFormData } from "@/features/contabil/hooks/useCentrosCusto";
import { usePlanoContasGerencial } from "@/features/contabil/hooks/usePlanoContasGerencial";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban, ChevronsUpDown, X } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function CentrosCusto() {
  const { centrosCusto, isLoading, createCentroCusto, updateCentroCusto, deleteCentroCusto } = useCentrosCusto();
  const { plano: contasGerenciais } = usePlanoContasGerencial();
  const [contaGerencialOpen, setContaGerencialOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CentroCusto | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CentroCustoFormData>({
    descricao: "", referencia: "", valorMaxUtilizacao: 0,
    percentualMaxUtilizacao: 0, percentualMaxAviso: 0, dtMaxUtilizacao: null,
    isAtivo: true, compoemCmv: false, compoemCustoGerencial: false, compoemCustoArmazena: false,
  });
  const [busca, setBusca] = useState("");

  const handleOpen = (item?: CentroCusto) => {
    setContaGerencialOpen(false);
    if (item) {
      setEditingItem(item);
      setFormData({
        descricao: item.descricao || "", referencia: item.referencia || "",
        valorMaxUtilizacao: item.valorMaxUtilizacao, percentualMaxUtilizacao: item.percentualMaxUtilizacao,
        percentualMaxAviso: item.percentualMaxAviso, dtMaxUtilizacao: item.dtMaxUtilizacao ?? null,
        contaContabilId: item.contaContabilId,
        isAtivo: item.isAtivo, compoemCmv: item.compoemCmv,
        compoemCustoGerencial: item.compoemCustoGerencial, compoemCustoArmazena: item.compoemCustoArmazena,
      });
    } else {
      setEditingItem(null);
      setFormData({ descricao: "", referencia: "", valorMaxUtilizacao: 0, percentualMaxUtilizacao: 0, percentualMaxAviso: 0, dtMaxUtilizacao: null, isAtivo: true, compoemCmv: false, compoemCustoGerencial: false, compoemCustoArmazena: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateCentroCusto.mutateAsync({ id: editingItem.id, ...formData });
    } else {
      await createCentroCusto.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  const centrosFiltrados = busca
    ? centrosCusto.filter(c =>
        c.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (c.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : centrosCusto;

  const paginacao = usePaginacaoLocal(centrosFiltrados);

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Centros de Custo</h1>
          <p className="text-muted-foreground">Gerencie os centros de custo</p>
        </div>
        <Button onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" />Novo Centro de Custo
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">CMV</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.descricao || "-"}</TableCell>
                <TableCell>{item.referencia || "-"}</TableCell>
                <TableCell className="text-center">{item.compoemCmv ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isAtivo ? "success" : "secondary"}>{item.isAtivo ? "Ativo" : "Inativo"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpen(item)}><Pencil className="h-4 w-4" /></Button>
                    {item.isAtivo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(item.id)}><Ban className="h-4 w-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{busca ? "Nenhum centro de custo encontrado para esta busca." : "Nenhum centro de custo cadastrado"}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Centro de Custo" : "Novo Centro de Custo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={formData.referencia} onChange={(e) => setFormData({ ...formData, referencia: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Val. Máx. Utilização</Label>
                <Input type="number" step="0.01" value={formData.valorMaxUtilizacao || ""} onChange={(e) => setFormData({ ...formData, valorMaxUtilizacao: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>% Máx. Utilização</Label>
                <Input type="number" step="0.01" value={formData.percentualMaxUtilizacao || ""} onChange={(e) => setFormData({ ...formData, percentualMaxUtilizacao: Number(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>% Máx. Aviso</Label>
                <Input type="number" step="0.01" value={formData.percentualMaxAviso || ""} onChange={(e) => setFormData({ ...formData, percentualMaxAviso: Number(e.target.value) })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Data Máxima de Utilização</Label>
              <Input
                type="date"
                value={formData.dtMaxUtilizacao ? formData.dtMaxUtilizacao.substring(0, 10) : ""}
                onChange={(e) => setFormData({ ...formData, dtMaxUtilizacao: e.target.value || null })}
              />
            </div>
            <div className="space-y-2">
              <Label>Conta Contábil Gerencial</Label>
              <Popover open={contaGerencialOpen} onOpenChange={setContaGerencialOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                    {formData.contaContabilId
                      ? contasGerenciais.find(c => c.id === formData.contaContabilId)?.descricaoConta ?? "Conta não encontrada"
                      : <span className="text-muted-foreground">Buscar conta gerencial...</span>
                    }
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {formData.contaContabilId && (
                        <X className="h-3 w-3 opacity-50 hover:opacity-100" onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, contaContabilId: null });
                        }} />
                      )}
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Digite para buscar..." />
                    <CommandList>
                      <CommandEmpty>Nenhuma conta encontrada.</CommandEmpty>
                      <CommandGroup>
                        {contasGerenciais.filter(c => !c.inativo).map(c => (
                          <CommandItem
                            key={c.id}
                            value={`${c.codigoPlanoConta ?? c.referencia ?? ""} ${c.descricaoConta ?? ""}`}
                            onSelect={() => {
                              setFormData({ ...formData, contaContabilId: c.id });
                              setContaGerencialOpen(false);
                            }}
                          >
                            <span className="text-muted-foreground text-xs mr-2">{c.codigoPlanoConta ?? c.referencia}</span>
                            {c.descricaoConta}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch id="cmv" checked={formData.compoemCmv} onCheckedChange={(v) => setFormData({ ...formData, compoemCmv: v })} />
                <Label htmlFor="cmv">Compõe CMV</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="gerencial" checked={formData.compoemCustoGerencial} onCheckedChange={(v) => setFormData({ ...formData, compoemCustoGerencial: v })} />
                <Label htmlFor="gerencial">Custo Gerencial</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="armazena" checked={formData.compoemCustoArmazena} onCheckedChange={(v) => setFormData({ ...formData, compoemCustoArmazena: v })} />
                <Label htmlFor="armazena">Custo Armazenagem</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ativo" checked={formData.isAtivo} onCheckedChange={(v) => setFormData({ ...formData, isAtivo: v })} />
                <Label htmlFor="ativo">Ativo</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar centro de custo</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar este centro de custo?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteCentroCusto.mutate(inativarId); setInativarId(null); } }}>Inativar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
